import { Suspense, lazy, ElementType } from 'react';
import { Navigate, useRoutes, useLocation } from 'react-router-dom';
// hooks
import useAuth, { UserRole } from '../hooks/useAuth';
// layouts
import DashboardLayout from '../layouts/dashboard';
import LogoOnlyLayout from '../layouts/LogoOnlyLayout';
// guards
import GuestGuard from '../guards/GuestGuard';
import AuthGuard from '../guards/AuthGuard';
import RoleBasedGuard from '../guards/RoleBasedGuard';
// config
import { PATH_AFTER_LOGIN } from '../config';
// components
import LoadingScreen from '../components/LoadingScreen';


// ----------------------------------------------------------------------

const Loadable = (Component: ElementType) => (props: any) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { pathname } = useLocation();

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { isAuthenticated } = useAuth();

  const isDashboard = pathname.includes('/dashboard') && isAuthenticated;

  return (
    <Suspense fallback={<LoadingScreen isDashboard={isDashboard} />}>
      <Component {...props} />
    </Suspense>
  );
};

export default function Router() {
  return useRoutes([
    {
      path: 'auth',
      children: [
        {
          path: 'login',
          element: (
            <GuestGuard>
              <Login />
            </GuestGuard>
          ),
        },
        {
          path: 'register',
          element: (
            <GuestGuard>
              <Register />
            </GuestGuard>
          ),
        },
        { path: 'login-unprotected', element: <Login /> },
        { path: 'register-unprotected', element: <Register /> },
        { path: 'reset-password', element: <ResetPassword /> },
        { path: 'new-password', element: <NewPassword /> },
        { path: 'verify', element: <VerifyCode /> },
      ],
    },

    // Dashboard Routes
    {
      path: 'dashboard',
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        { element: <Navigate to={PATH_AFTER_LOGIN} replace />, index: true },
        {
          path: 'home',
          element: (
            <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR, UserRole.RETAILER, UserRole.STAFF]}>
              <Home />
            </RoleBasedGuard>
          )
        },
        {
          path: 'products',
          children: [
            {
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN]} feature="products">
                  <Navigate to="/dashboard/products/all" replace />
                </RoleBasedGuard>
              ),
              index: true
            },
            {
              path: 'all',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN]} feature="products">
                  <GeneralProductView />
                </RoleBasedGuard>
              )
            },
            {
              path: 'add',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN]} feature="products">
                  <ProductCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'edit/:id',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN]} feature="products">
                  <ProductEdit />
                </RoleBasedGuard>
              )
            },
            {
              path: 'import-product',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN]} feature="products">
                  <ProductImportPage />
                </RoleBasedGuard>
              )
            },
            {
              path: 'import-rates',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN]} feature="products">
                  <ProductRateImportPage />
                </RoleBasedGuard>
              )
            },
          ]
        },
        {
          path: 'companies',
          children: [
            {
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]}>
                  <Navigate to="/dashboard/companies/all" replace />
                </RoleBasedGuard>
              ),
              index: true
            },
            {
              path: 'all',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]}>
                  <CompanyList />
                </RoleBasedGuard>
              )
            },
            {
              path: 'add',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]}>
                  <CompanyCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'edit/:id',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]}>
                  <CompanyEdit />
                </RoleBasedGuard>
              )
            },
            {
              path: 'profile',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR, UserRole.RETAILER]}>
                  <CompanyProfile />
                </RoleBasedGuard>
              )
            },
            {
              path: 'import',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]}>
                  <CompanyImportPage />
                </RoleBasedGuard>
              )
            },
            {
              path: 'manage-products/:id',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]}>
                  <CompanyProductManage />
                </RoleBasedGuard>
              )
            },
          ]
        },
        // Orders section - accessible by Retailer and Staff
        {
          path: 'orders',
          children: [
            {
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.RETAILER, UserRole.DISTRIBUTOR, UserRole.STAFF]} feature="orders">
                  <Navigate to="/dashboard/orders/all" replace />
                </RoleBasedGuard>
              ),
              index: true
            },
            {
              path: 'all',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR, UserRole.RETAILER, UserRole.STAFF]} feature="orders">
                  <OrderList />
                </RoleBasedGuard>
              )
            },
            {
              path: 'edit/:id/:date',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.RETAILER, UserRole.DISTRIBUTOR, UserRole.STAFF]} feature="orders">
                  <OrderEdit />
                </RoleBasedGuard>
              )
            },
            {
              path: 'summary/:id/:date',
              element: (
                <RoleBasedGuard roles={[UserRole.DISTRIBUTOR]} feature="orders">
                  <OrderDistributorSummary />
                </RoleBasedGuard>
              )
            },
            {
              path: 'manage',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR, UserRole.STAFF]} feature="orders">
                  <OrderManage />
                </RoleBasedGuard>
              )
            },
          ]
        },
        {
          path: 'users',
          children: [
            {
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]} feature="users">
                  <Navigate to="/dashboard/users/all" replace />
                </RoleBasedGuard>
              ),
              index: true
            },
            {
              path: 'all',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]} feature="users">
                  <UserList />
                </RoleBasedGuard>
              )
            },
            {
              path: 'add',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]} feature="users">
                  <UserCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'edit/:id',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]} feature="users">
                  <UserEdit />
                </RoleBasedGuard>
              )
            },
            {
              path: 'account',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR, UserRole.RETAILER]} feature="users">
                  <UserAccount />
                </RoleBasedGuard>
              )
            },
            {
              path: 'import',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN]} feature="users">
                  <UserImportPage />
                </RoleBasedGuard>
              )
            },
          ],
        },
        {
          path: 'account',
          children: [
            {
              element: (
                <RoleBasedGuard roles={[UserRole.DISTRIBUTOR, UserRole.RETAILER]}>
                  <Navigate to="/dashboard/account/info" replace />
                </RoleBasedGuard>
              ),
              index: true
            },
            {
              path: 'info',
              element: (
                <RoleBasedGuard roles={[UserRole.DISTRIBUTOR, UserRole.RETAILER]}>
                  <AccountInfo />
                </RoleBasedGuard>
              )
            },
            {
              path: 'bulk-payment',
              element: (
                <RoleBasedGuard roles={[UserRole.DISTRIBUTOR]}>
                  <RetailerBulkPayment />
                </RoleBasedGuard>
              )
            },
            {
              path: 'reports',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN]}>
                  <Reports />
                </RoleBasedGuard>
              )
            },
          ]
        },
        {
          path: 'vehicles',
          children: [
            {
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]} feature="vehicles">
                  <Navigate to="/dashboard/vehicles/all" replace />
                </RoleBasedGuard>
              ),
              index: true
            },
            {
              path: 'all',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]} feature="vehicles">
                  <VehicleList />
                </RoleBasedGuard>
              )
            },
            {
              path: 'add',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]} feature="vehicles">
                  <VehicleCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'edit/:id',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]} feature="vehicles">
                  <VehicleEdit />
                </RoleBasedGuard>
              )
            },
            {
              path: 'import',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]} feature="vehicles">
                  <VehicleImportPage />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'workers',
          children: [
            {
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]} feature="workers">
                  <Navigate to="/dashboard/workers/all" replace />
                </RoleBasedGuard>
              ),
              index: true
            },
            {
              path: 'all',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]} feature="workers">
                  <WorkerList />
                </RoleBasedGuard>
              )
            },
            {
              path: 'add',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]} feature="workers">
                  <WorkerCreate />
                </RoleBasedGuard>
              )
            },
            {
              path: 'edit/:id',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]} feature="workers">
                  <WorkerEdit />
                </RoleBasedGuard>
              )
            },
            {
              path: 'import',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN, UserRole.DISTRIBUTOR]} feature="workers">
                  <WorkerImportPage />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'newsletters',
          children: [
            {
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN]}>
                  <NewsLetterList />
                </RoleBasedGuard>
              ),
              index: true
            },
            {
              path: 'create',
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN]}>
                  <NewsLetterCreate />
                </RoleBasedGuard>
              )
            }
          ]
        },
        {
          path: 'subscriptions',
          children: [
            {
              element: (
                <RoleBasedGuard roles={[UserRole.ADMIN]}>
                  <SubscriptionList />
                </RoleBasedGuard>
              ),
              index: true
            }
          ]
        },
      ],
    },

    // Main Routes
    {
      path: '*',
      element: <LogoOnlyLayout />,
      children: [
        { path: 'coming-soon', element: <ComingSoon /> },
        { path: 'maintenance', element: <Maintenance /> },
        { path: '500', element: <Page500 /> },
        { path: '404', element: <Page404 /> },
        { path: '403', element: <Page403 /> },
        { path: '*', element: <Navigate to="/404" replace /> },
      ],
    },
    {
      path: '/',
      element: (
        <AuthGuard>
          <Navigate to="/dashboard" replace />
        </AuthGuard>
      ),
    },
    { path: '*', element: <Navigate to="/404" replace /> },
  ]);
}

// AUTHENTICATION
const Login = Loadable(lazy(() => import('../pages/auth/Login')));
const Register = Loadable(lazy(() => import('../pages/auth/Register')));
const ResetPassword = Loadable(lazy(() => import('../pages/auth/ResetPassword')));
const NewPassword = Loadable(lazy(() => import('../pages/auth/NewPassword')));
const VerifyCode = Loadable(lazy(() => import('../pages/auth/VerifyCode')));

// DASHBOARD
const Home = Loadable(lazy(() => import('../pages/dashboard/home/Home')));
const GeneralProductView = Loadable(lazy(() => import('../pages/dashboard/product/ProductList')));
const ProductCreate = Loadable(lazy(() => import('../pages/dashboard/product/ProductCreate')));
const ProductEdit = Loadable(lazy(() => import('../pages/dashboard/product/ProductEdit')));
const ProductImportPage = Loadable(lazy(() => import('../pages/dashboard/product/ProductImportPage')));
const ProductRateImportPage = Loadable(lazy(() => import('../pages/dashboard/product/ProductRateImportPage')));
const CompanyCreate = Loadable(lazy(() => import('../pages/dashboard/company/CompanyCreate')));
const CompanyImportPage = Loadable(lazy(() => import('../pages/dashboard/company/CompanyImportPage')));
const CompanyList = Loadable(lazy(() => import('../pages/dashboard/company/CompanyList')));
const UserAccount = Loadable(lazy(() => import('../pages/dashboard/user/UserAccount')));
const CompanyEdit = Loadable(lazy(() => import('src/pages/dashboard/company/CompanyEdit')));
const CompanyProfile = Loadable(lazy(() => import('src/pages/dashboard/company/CompanyProfile')));
const CompanyProductManage = Loadable(lazy(() => import('src/pages/dashboard/company/CompanyProductManagePage')));
const UserImportPage = Loadable(lazy(() => import('src/pages/dashboard/user/UserImportPage')));
const UserList = Loadable(lazy(() => import('src/pages/dashboard/user/UserList')));
const UserCreate = Loadable(lazy(() => import('src/pages/dashboard/user/UserCreate')));
const UserEdit = Loadable(lazy(() => import('src/pages/dashboard/user/UserEdit')));
const VehicleList = Loadable(lazy(() => import('src/pages/dashboard/vehicle/VehicleList')));
const VehicleCreate = Loadable(lazy(() => import('src/pages/dashboard/vehicle/VehicleCreate')));
const VehicleEdit = Loadable(lazy(() => import('src/pages/dashboard/vehicle/VehicleEdit')));
const VehicleImportPage = Loadable(lazy(() => import('src/pages/dashboard/vehicle/VehicleImportPage')));
const WorkerList = Loadable(lazy(() => import('src/pages/dashboard/worker/WorkerList')));
const WorkerCreate = Loadable(lazy(() => import('src/pages/dashboard/worker/WorkerCreate')));
const WorkerEdit = Loadable(lazy(() => import('src/pages/dashboard/worker/WorkerEdit')));
const WorkerImportPage = Loadable(lazy(() => import('src/pages/dashboard/worker/WorkerImportPage')));
const NewsLetterList = Loadable(lazy(() => import('../pages/dashboard/newsletter/NewsLetterList')));
const NewsLetterCreate = Loadable(lazy(() => import('../pages/dashboard/newsletter/NewsLetterCreate')));
const SubscriptionList = Loadable(lazy(() => import('../pages/dashboard/subscription/SubscriptionList')));
const AccountInfo = Loadable(lazy(() => import('../pages/dashboard/account/AccountInfo')));
const RetailerBulkPayment = Loadable(lazy(() => import('../pages/dashboard/account/RetailerBulkPayment')));
const Reports = Loadable(lazy(() => import('../pages/dashboard/account/Reports')));


// ORDER PAGES (You'll need to create these)
const OrderList = Loadable(lazy(() => import('../pages/dashboard/order/OrderList')));
// const OrderCreate = Loadable(lazy(() => import('../pages/dashboard/order/OrderCreate')));
const OrderEdit = Loadable(lazy(() => import('../pages/dashboard/order/OrderEdit')));
const OrderManage = Loadable(lazy(() => import('../pages/dashboard/order/OrderManage')));
const OrderDistributorSummary = Loadable(lazy(() => import('../pages/dashboard/order/OrderDistributorSummary')));

// MAIN
const ComingSoon = Loadable(lazy(() => import('../pages/ComingSoon')));
const Maintenance = Loadable(lazy(() => import('../pages/Maintenance')));
const Page500 = Loadable(lazy(() => import('../pages/Page500')));
const Page403 = Loadable(lazy(() => import('../pages/Page403')));
const Page404 = Loadable(lazy(() => import('../pages/Page404')));
